import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

export function OtpCardTab() {
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    isError: isSettingsError,
    error: settingsError,
  } = useQuery({
    queryKey: ["otp-card-settings"],
    queryFn: () => api.getOtpCardSettings(),
    // Isti razlog kao MinimaxTab - ako modul "otp_card" nije aktivan za
    // firmu (RequireModule guard na backendu vraća 403), nema smisla
    // ponavljati zahtev.
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState<"TEST" | "PRODUCTION">("TEST");
  const [storeName, setStoreName] = useState("");
  const [currency, setCurrency] = useState("941");
  const [sharedSecret, setSharedSecret] = useState("");
  const [sharedSecretApi, setSharedSecretApi] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.otpCardEnabled);
      setEnvironment(settings.otpCardEnvironment);
      setStoreName(settings.otpCardStoreName ?? "");
      setCurrency(settings.otpCardCurrency);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateOtpCardSettings({
        otpCardEnabled: enabled,
        otpCardEnvironment: environment,
        otpCardStoreName: storeName || undefined,
        otpCardCurrency: currency || undefined,
        otpCardSharedSecret: sharedSecret || undefined,
        otpCardSharedSecretApi: sharedSecretApi || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["otp-card-settings"], updated);
      setSharedSecret("");
      setSharedSecretApi("");
    },
  });

  if (isSettingsError) {
    if (settingsError instanceof ApiError && settingsError.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={settingsError} moduleKey="otp_card" />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {settingsError instanceof Error ? settingsError.message : "Učitavanje nije uspelo."}
      </p>
    );
  }

  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Kartice - OTP banka</h2>
        <p className="text-xs text-slate-400">
          Naplata faktura platnim karticama preko OTP Banka VPOS sistema. Kupac se preusmerava na
          hostovanu stranicu banke (unos podataka kartice, 3D Secure), pa se vraća nazad na
          fakturu sa ishodom. Naziv prodavnice i oba shared secret ključa dobijaš od banke uz
          ugovor o prihvatanju kartica.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Aktivna naplata karticama za ovu firmu
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">Okruženje</label>
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as "TEST" | "PRODUCTION")}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="TEST">Test (virtualpostest.sia.eu)</option>
          <option value="PRODUCTION">Produkcija (virtualpos.sia.eu)</option>
        </select>
        <p className="mt-1 text-xs text-slate-400">
          Ako menjaš iz test u produkcijsko okruženje (ili obrnuto), obavezno promeni i naziv
          prodavnice i oba shared secret ključa - banka ih izdaje odvojeno po okruženju.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">Naziv prodavnice (SHOPID)</label>
          <input
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            placeholder="npr. HOSTARENA"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Šifra valute</label>
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder="941 (RSD)"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Shared Secret (za slanje zahteva)</label>
        <input
          value={sharedSecret}
          onChange={(e) => setSharedSecret(e.target.value)}
          type="password"
          placeholder={settings.hasOtpCardSharedSecret ? "•••••••••• (nepromenjeno)" : ""}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        {settings.hasOtpCardSharedSecret && (
          <p className="mt-1 text-xs text-slate-400">Trenutno je unet ključ - ostavi prazno da ga zadržiš.</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Shared Secret Key Result/API (za proveru odgovora)</label>
        <input
          value={sharedSecretApi}
          onChange={(e) => setSharedSecretApi(e.target.value)}
          type="password"
          placeholder={settings.hasOtpCardSharedSecretApi ? "•••••••••• (nepromenjeno)" : ""}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        {settings.hasOtpCardSharedSecretApi && (
          <p className="mt-1 text-xs text-slate-400">Trenutno je unet ključ - ostavi prazno da ga zadržiš.</p>
        )}
      </div>

      {save.isError && save.error instanceof ApiError && save.error.response.errorCode === "MODULE_NOT_AVAILABLE" ? (
        <ModuleGateNotice error={save.error} moduleKey="otp_card" />
      ) : (
        save.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {save.error instanceof Error ? save.error.message : "Čuvanje nije uspelo."}
          </p>
        )
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <Button disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
    </div>
  );
}
